import { useEffect, useRef } from 'react';

import { localTime, controlAPICall, ALL_MACHINES } from '@helpers';
import { scale } from '@components/frame';
import { TimePeriod } from '@v2/pages/machines/common/enums';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { getBarLegend } from '@components/machines/components/eventsChart/calculation';
import { getEightWeeksWindow, getStatsRequestParams } from '@components/machines/components/eventsChart/helpers/period';
import { OEE_LABEL_MIN_BAR_HEIGHT } from '@components/machines/components/Utilization/components/chart/components/constants';
import { formatBarData } from '@components/machines/components/Utilization/components/chart/components/helpers';
import { UseManageUtilizationBarParams, UseManageUtilizationBarReturn, UtilizationBarDataItem } from '@components/machines/components/Utilization/components/chart/types';

export const useManageUtilizationBar = ({
  item: { date, data = {} },
  id,
  barBlockWidth,
  selectedDate,
  saveChartData,
  getMachineStatsOeeTeepPerPeriod,
}: UseManageUtilizationBarParams): UseManageUtilizationBarReturn => {
  const abortController = useRef<AbortController | null>(null);

  const { timePeriod: selectedTime } = useHandleParams();

  const loadData = async () => {
    const d: Record<string, unknown> = { ...getStatsRequestParams(selectedTime, localTime(date)) };
    if (id !== ALL_MACHINES) {
      d.machineId = id;
    } else {
      d.forAllMachines = true;
    }

    const result = await controlAPICall(getMachineStatsOeeTeepPerPeriod, d, abortController);

    saveChartData(date, {
      data: formatBarData(result, date, selectedTime),
      loaded: true,
    });
  };

  useEffect(() => {
    if (!data.loaded) {
      loadData();
    }
  }, [data.loaded, date, id]);

  // SF-5106: in eightWeeks mode `selectedDate` is the LAST bar's anchor; getBarLegend expects
  // the FIRST week's date to step 8 forward labels (W{n}..W{n+7}). Convert anchor → window-start
  // so the X-axis reads W{anchor-7}..W{anchor} instead of W{anchor}..W{anchor+7}.
  const legendStart =
    selectedTime === TimePeriod.EIGHT_WEEKS ? getEightWeeksWindow(localTime(selectedDate)).start.format() : selectedDate;
  const { legend = [], legendRules = [] } = getBarLegend(selectedTime, legendStart);

  // Position by dateISO, never the i18n display label `date`: Hermes (native) can't parse a
  // localized "01 Jan 2026" string, so month() returns NaN and every bar collapses into one slot.
  const getBarPosition = (item: UtilizationBarDataItem, index: number) => {
    if (selectedTime === TimePeriod.YEAR && item.dateISO) {
      const monthIndex = localTime(item.dateISO).month();
      return monthIndex * barBlockWidth;
    }
    return index * barBlockWidth;
  };

  // 8-week mode + data loaded + bar tall enough to not collide with top edge.
  const isOeeLabelVisible = (barItem: UtilizationBarDataItem, chartHeight: number): boolean =>
    selectedTime === TimePeriod.EIGHT_WEEKS &&
    !!data.loaded &&
    (barItem.oee || 0) * chartHeight >= scale(OEE_LABEL_MIN_BAR_HEIGHT);

  return {
    selectedTime,
    legend,
    legendRules,
    getBarPosition,
    isOeeLabelVisible,
  };
};
